import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

function fmtDay(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return {
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
    day: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  };
}

export const AvailabilityGrid = ({ courtId, onPick, refreshKey }) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const nowHour = new Date().getHours();
  const [start, setStart] = useState(todayStr);
  const [days, setDays] = useState([]);

  useEffect(() => {
    api.get(`/courts/${courtId}/week`, { params: { start } }).then((r) => setDays(r.data.days)).catch(() => {});
  }, [courtId, start, refreshKey]);

  const shift = (n) => {
    const d = new Date(start + "T00:00:00");
    d.setDate(d.getDate() + n);
    const v = d.toISOString().slice(0, 10);
    setStart(v < todayStr ? todayStr : v);
  };

  if (days.length === 0) return null;
  const times = days[0].slots.map((s) => s.time);

  return (
    <div data-testid="availability-grid">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <h2 className="font-display uppercase text-2xl lg:text-3xl tracking-tight">
          This week <span className="text-[#ccff00]">at a glance</span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => shift(-7)}
            disabled={start <= todayStr}
            data-testid="week-prev-btn"
            aria-label="Previous week"
            className="w-10 h-10 rounded-full border border-white/20 text-white/70 flex items-center justify-center hover:border-[#ccff00]/60 hover:text-[#ccff00] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => shift(7)}
            data-testid="week-next-btn"
            aria-label="Next week"
            className="w-10 h-10 rounded-full border border-white/20 text-white/70 flex items-center justify-center hover:border-[#ccff00]/60 hover:text-[#ccff00] transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-[#2a2a2a] rounded-md bg-[#0f0f10]">
        <div className="min-w-[720px] p-4">
          <div className="grid gap-1" style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}>
            <div />
            {days.map((d) => {
              const f = fmtDay(d.date);
              return (
                <div key={d.date} className="text-center pb-2">
                  <p className={`text-xs uppercase tracking-widest ${d.date === todayStr ? "text-[#ccff00]" : "text-white/50"}`}>{f.weekday}</p>
                  <p className="text-[11px] text-white/40">{f.day}</p>
                </div>
              );
            })}
            {times.map((t) => (
              <div key={t} className="contents">
                <div className="text-[11px] text-white/40 font-mono pr-2 flex items-center justify-end">{t}</div>
                {days.map((d) => {
                  const slot = d.slots.find((s) => s.time === t);
                  const past = d.date === todayStr && parseInt(t, 10) <= nowHour;
                  const disabled = slot.booked || past;
                  return (
                    <button
                      key={d.date + t}
                      disabled={disabled}
                      onClick={() => onPick(d.date, t)}
                      data-testid={`grid-slot-${d.date}-${t}`}
                      aria-label={`${d.date} ${t} ${slot.booked ? "booked" : past ? "past" : "open"}`}
                      title={slot.booked ? "Booked" : past ? "Past" : `Book ${t}`}
                      className={`h-9 sm:h-7 rounded-sm transition-colors ${
                        slot.booked
                          ? "bg-[#2a2a2a] cursor-not-allowed"
                          : past
                            ? "bg-[#161616] cursor-not-allowed"
                            : "bg-[#ccff00]/15 hover:bg-[#ccff00] border border-[#ccff00]/20"
                      }`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-6 mt-4 text-xs text-white/50">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#ccff00]/15 border border-[#ccff00]/20" /> Open — click to book</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#2a2a2a]" /> Booked</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#161616]" /> Past</span>
      </div>
    </div>
  );
};

export default AvailabilityGrid;
